/**
 * Wave 18 — Live AI/ops KPI bus with SSE preference + poll fallback.
 * tip599 ops-live uses the same stream;.
 */
import { useEffect, useState } from "react";
import { apiBase, path, usePolled } from "./api.js";

/**
 * @param {"admin"|"sa"} scope
 * @param {number} everyMs poll cadence when SSE is down
 * @param {boolean} enabled Soft≠Live: only hit Live bus when ops_dashboard_eligible
 */
export function useGlobalMetricsLive(scope, everyMs = 20000, enabled = true) {
  const pollPath = enabled
    ? path("get__admin_ops_global-metrics", { query: { scope } })
    : "";
  const poll = usePolled(pollPath, enabled ? everyMs : 0);
  const [sseData, setSseData] = useState(null);
  const [transport, setTransport] = useState(enabled ? "poll" : "off");

  useEffect(() => {
    if (!enabled) {
      setSseData(null);
      setTransport("off");
      return undefined;
    }
    let dead = false;
    const ac = new AbortController();
    const url = apiBase() + path("get__admin_ops_global-metrics_stream", {
      query: { scope, interval_ms: Math.max(5000, Number(everyMs) || 20000) },
    });
    (async () => {
      try {
        const r = await fetch(url, {
          method: "GET",
          credentials: "include",
          headers: {
            Accept: "text/event-stream",
            "Cache-Control": "no-store",
          },
          signal: ac.signal,
        });
        if (!r.ok || !r.body || typeof r.body.getReader !== "function") {
          throw new Error(String(r.status || "stream unsupported"));
        }
        if (!dead) setTransport("sse");
        const reader = r.body.getReader();
        const dec = new TextDecoder();
        let buf = "";
        while (!dead) {
          const { done, value } = await reader.read();
          if (done) break;
          buf += dec.decode(value, { stream: true });
          const parts = buf.split("\n\n");
          buf = parts.pop() || "";
          for (const chunk of parts) {
            let event = "message";
            let data = "";
            for (const line of chunk.split("\n")) {
              if (line.startsWith("event:")) event = line.slice(6).trim();
              else if (line.startsWith("data:")) data += (data ? "\n" : "") + line.slice(5).trim();
            }
            if (event === "ping" || !data) continue;
            try {
              const json = JSON.parse(data);
              if (!dead) setSseData(json);
            } catch (_) { /* ignore malformed */ }
          }
        }
        if (!dead) setTransport("poll");
      } catch (e) {
        if (!dead && !(e && e.name === "AbortError")) setTransport("poll");
      }
    })();
    return () => {
      dead = true;
      ac.abort();
    };
  }, [scope, everyMs, enabled]);

  const live = enabled ? (sseData || poll.data) : null;
  return {
    data: live,
    error: !enabled ? "" : (sseData ? "" : (poll.error || "")),
    loading: enabled && !live && poll.loading,
    refreshing: enabled ? poll.refreshing : false,
    reload: poll.reload,
    transport: !enabled ? "off" : (sseData ? "sse" : transport),
    enabled: !!enabled,
  };
}
